// The machine at the end of the tab strip (canvas 4b): "■ ON", and on hover or click a card with the machine and its
// size, its load over the last two minutes (the Machine view's head and usage card, small), and the connection (what the status bar used to carry: link and transport, round trip, echo, network).
// A click (or Enter, Space) pins the card: it then closes only with Esc or a click outside, never because the pointer left.
// The chip and the card's pill say one status (machineState); while the connection is lost the card says why.

import { IconArrowUpRight } from '@tabler/icons-react';
import { useEffect, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { machineUsage, type UsageSample } from '../api';
import { tierLabel } from '../pages/Shell';
import { PixelMeter } from './PixelMeter';
import { CARD_SPAN, gb, ofGb, pct, recent, Spark } from './Usage';
import { telemetry, type Stats } from '../net/telemetry';
import { cmxSide, fmtMs } from '../net/quality';
import { conn as connection } from '../net/connection';
import type { MachineLoad } from '../net/protocol';
import { machineName, useStore, type ConnState } from '../store';
import { Button, Ic, Mark, Meta, Mini, Mono, Pix, presence, usePresence } from './ds';
import { useOffline } from './ConnectionNotice';
import { useActions } from './Workspace';

let cached: Stats = telemetry.stats();
const subscribeStats = (cb: () => void) =>
  telemetry.subscribe(() => {
    cached = telemetry.stats();
    cb();
  });
const ms = (v: number | null) => (v === null ? '–' : `${v < 10 ? v.toFixed(1) : Math.round(v)} ms`);

/** "Jakarta (asia-southeast2)" → "Jakarta" */
export const regionCity = (r?: string) => (r ? r.replace(/\s*\(.*\)\s*$/, '') : '');

type Tone = 'live' | 'attn' | 'fault' | 'idle';

/** One status for the chip and the card's pill: the network first, then the machine, then the link to it, then its
 *  load (busy or full, spec "Machine load": the banner's colour while it lasts, hidden or not). */
function machineState(conn: ConnState, offline: boolean, status: string, load: MachineLoad['state']): { tone: Tone; word: string; blink?: boolean } {
  if (offline) return { tone: 'idle', word: 'offline' };
  if (status !== 'running') return { tone: 'idle', word: status };
  if (conn === 'reconnecting') return { tone: 'attn', word: 'reconnecting', blink: true };
  if (conn === 'connecting') return { tone: 'attn', word: 'connecting' };
  if (conn === 'unauthorized') return { tone: 'fault', word: 'signed out' };
  if (conn === 'idle') return { tone: 'idle', word: 'offline' };
  return loadLook(load);
}

/** A running machine as the chip, the card's pill and the Machine view's badge show it: on, busy or full. */
export const loadLook = (load: MachineLoad['state']): { tone: 'live' | 'attn' | 'fault'; word: string } =>
  load === 'full' ? { tone: 'fault', word: 'full' } : load === 'busy' ? { tone: 'attn', word: 'busy' } : { tone: 'live', word: 'on' };

const CONN = { idle: 'Offline', connecting: 'Connecting…', connected: 'Connected', reconnecting: 'Reconnecting…', unauthorized: 'Signed out' } as const;

/** While the card is open: the machine's last two minutes, again every 5 s (the agent's step), asked from a little
 *  further back and kept by the machine's own clock. */
function useRecentUsage(on: boolean) {
  const [s, setS] = useState<UsageSample[]>([]);
  useEffect(() => {
    if (!on) return;
    let live = true;
    const get = () =>
      void machineUsage('1h', Math.floor(Date.now() / 1000) - CARD_SPAN - 30).then((r) => {
        if (live && r.ok) setS(recent(r.data, CARD_SPAN));
      });
    get();
    const t = setInterval(get, 5000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [on]);
  return s;
}

/** `.ws-mpop-stats` cell: the Machine view's stat, small: the figure, what it is of, and its last two minutes. */
const Cell = ({ k, value, of, children }: { k: string; value: string; of: string; children: ReactNode }) => (
  <span data-testid={`machine-pop-${k.toLowerCase()}`}>
    <Pix>{k}</Pix>
    <span className="ws-mpop-stat">{value}</span>
    <span className="ws-mpop-of">{of || '\u00a0'}</span>
    <span className="ws-mpop-chart">{children}</span>
  </span>
);

const Row = ({ k, children, sub, right }: { k: string; children: ReactNode; sub?: string; right?: ReactNode }) => (
  <div className="ws-mrow">
    <span className="ws-mrow-k">
      <Pix>{k}</Pix>
    </span>
    <span className="ws-mrow-v">
      {children}
      {sub && <Meta>{sub}</Meta>}
    </span>
    {right}
  </div>
);

export function MachineChip() {
  const a = useActions();
  const machine = useStore((s) => s.me?.machine);
  const conn = useStore((s) => s.conn);
  const connError = useStore((s) => s.connError);
  const transport = useStore((s) => s.transport);
  const rtt = useStore((s) => s.rtt);
  const net = useStore((s) => s.net);
  const offline = useOffline();
  const load = useStore((s) => s.machineLoad);
  const stats = useSyncExternalStore(subscribeStats, () => cached);
  const [open, setOpen] = useState(false);
  const p = usePresence(open, 120);
  const samples = useRecentUsage(open);
  const usage = samples.at(-1) ?? null;
  const ref = useRef<HTMLDivElement>(null);
  const hover = useRef<number>(0);
  // Pinned by a click: the pointer leaving doesn't close it; only Esc or a click outside does.
  const pinned = useRef(false);
  // Opened from the keyboard: focus moves into the card once it is there.
  const focusIn = useRef(false);
  // Closed with Escape: hovering doesn't open it again until the pointer has left (taking the
  // card away from under the pointer reads as entering the chip again).
  const dismissed = useRef(false);

  const hide = () => {
    clearTimeout(hover.current);
    pinned.current = false;
    setOpen(false);
  };

  useEffect(() => {
    if (!p.mounted || !focusIn.current) return;
    focusIn.current = false;
    ref.current?.querySelector<HTMLElement>('.ws-mpop')?.focus({ preventScroll: true });
  }, [p.mounted, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => ref.current && !ref.current.contains(e.target as Node) && hide();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // focus back on the chip, if it was in the card (or nowhere)
      const at = document.activeElement;
      if (!at || at === document.body || ref.current?.contains(at)) ref.current?.querySelector<HTMLElement>('.ws-mchip')?.focus();
      dismissed.current = true;
      hide();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!machine) return null;
  const connected = conn === 'connected';
  const city = regionCity(machine.region);
  // its size, as the Machine view's head says it: "STANDARD L · 8 vCPU · 32 GB · 400 GB", wrapping only between its parts
  const specs = [
    machine.tier && tierLabel(machine.tier).toUpperCase(),
    machine.vcpu && `${machine.vcpu} vCPU`,
    machine.memoryGB && `${machine.memoryGB} GB`,
    machine.diskGB && `${machine.diskGB} GB`,
  ]
    .filter((x): x is string => !!x)
    .map((x) => x.replaceAll(' ', '\u00a0'))
    .join(' · ');
  const st = machineState(conn, offline, machine.status, load.state);
  // hover opens after a beat and closes after a beat, so passing over it does nothing
  const enter = () => {
    clearTimeout(hover.current);
    if (dismissed.current || pinned.current) return;
    hover.current = window.setTimeout(() => setOpen(true), 150);
  };
  // Leaving toward the card (or back to the chip): inside the cone from where the pointer left to the card's far
  // corners it stays open a beat longer with each move, so a diagonal path across the gap doesn't close it; out of the
  // cone it closes at once. The card is a child here, so leaving means leaving both.
  const leave = (e: ReactPointerEvent<HTMLDivElement>) => {
    clearTimeout(hover.current);
    dismissed.current = false;
    if (pinned.current) return;
    const pop = ref.current?.querySelector<HTMLElement>('.ws-mpop')?.getBoundingClientRect();
    if (!open || !pop) {
      hover.current = window.setTimeout(hide, 200);
      return;
    }
    const apex = { x: e.clientX, y: e.clientY };
    const stop = () => document.removeEventListener('pointermove', move);
    const wait = (ms: number) => {
      clearTimeout(hover.current);
      hover.current = window.setTimeout(() => {
        stop();
        hide();
      }, ms);
    };
    const move = (m: globalThis.PointerEvent) => {
      if (ref.current?.contains(m.target as Node)) return stop(); // back on the chip or the card
      wait(inCone(apex, pop, m.clientX, m.clientY) ? 450 : 80);
    };
    document.addEventListener('pointermove', move);
    wait(450);
  };

  return (
    <div className="ws-machine" ref={ref} onPointerEnter={(e) => e.pointerType === 'mouse' && enter()} onPointerLeave={(e) => e.pointerType === 'mouse' && leave(e)}>
      <button
        type="button"
        className="ws-mchip"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${machine.id} is ${st.word}. Show machine details`}
        data-testid="machine"
        data-state={offline ? 'offline' : conn}
        data-tone={st.tone}
        onClick={(e) => {
          // a click after hovering keeps it open (pinned), and doesn't toggle it; from the keyboard (no pointer: detail 0) focus follows into the card
          clearTimeout(hover.current);
          pinned.current = true;
          if (e.detail === 0 && !open) focusIn.current = true;
          setOpen(true);
        }}
      >
        <Mark tone={st.tone} blink={st.blink} />
        <Mono className="ws-mchip-word">{st.word}</Mono>
      </button>
      {p.mounted && (
        <div className="ws-mpop ds-float ds-pop" role="dialog" aria-label={machine.id} tabIndex={-1} {...presence(p)} data-testid="machine-pop">
          <div className="ws-mpop-head">
            <Mini on={machine.status === 'running' && connected} />
            <span className="ws-mpop-name">
              <span className="ws-mpop-id">{machine.id}</span>
              <Meta>{[city, machine.status === 'running' && connected ? 'always on' : ''].filter(Boolean).join(' · ')}</Meta>
              {specs && (
                <span className="ds-meta ws-mpop-specs" data-testid="machine-pop-specs">
                  {specs}
                </span>
              )}
            </span>
            <span className="ws-pill" data-tone={st.tone} data-testid="machine-pill">
              <Mark tone={st.tone} blink={st.blink} />
              {st.word}
            </span>
          </div>
          {(st.word === 'reconnecting' || st.word === 'offline') && (
            <div className="ws-mpop-note" data-tone={st.tone} role="status" data-testid={`conn-${st.word}`}>
              <span className="ws-notice-text">
                <span className="ws-notice-title">{st.word === 'offline' ? 'You’re offline' : `Reconnecting to ${machineName()}…`}</span>
                <span className="ws-notice-body">{st.word === 'offline' ? 'Reconnects when the network is back.' : 'Your sessions keep running.'}</span>
              </span>
              {st.word === 'offline' && (
                <Button size="sm" onClick={() => connection.retryNow()}>
                  Try now
                </Button>
              )}
            </div>
          )}
          {(st.word === 'busy' || st.word === 'full') && (
            <div className="ws-mpop-note" data-tone={st.tone} role="status" data-testid="machine-pop-load">
              <span className="ws-notice-text">
                <span className="ws-notice-title">{load.title}</span>
                {load.detail && <span className="ws-notice-body">{load.detail}</span>}
              </span>
            </div>
          )}
          <div className="ws-mpop-stats">
            <Cell k="CPU" value={usage ? pct(usage.cpu) : '–'} of={usage ? `of ${usage.cpus} cores` : ''}>
              <Spark vals={samples.map((s) => s.cpu)} label="CPU" h={28} />
            </Cell>
            <Cell k="Memory" value={usage ? gb(usage.memUsed) : '–'} of={usage ? ofGb(usage.memTotal) : ''}>
              <Spark vals={samples.map((s) => s.memUsed)} label="Memory" h={28} />
            </Cell>
            <Cell k="Disk" value={usage ? gb(usage.diskUsed) : '–'} of={usage ? ofGb(usage.diskTotal) : ''}>
              <PixelMeter share={usage?.diskTotal ? usage.diskUsed / usage.diskTotal : 0} label="Disk used" rows={3} className="ws-mpop-meter" />
            </Cell>
          </div>
          <div className="ws-mpop-conn">
            <Row
              k="Link"
              right={
                connected && transport ? (
                  <span className="ws-tag" title={transport === 'wt' ? 'WebTransport (QUIC)' : 'WebSocket'} data-testid="transport">
                    <Mono>{transport}</Mono>
                  </span>
                ) : undefined
              }
            >
              <span className="ws-inline" data-testid="conn-state" data-state={conn} title={connError ?? ''}>
                <Mark tone={offline ? 'idle' : connected ? 'live' : conn === 'reconnecting' || conn === 'connecting' ? 'attn' : 'fault'} blink={!offline && conn === 'reconnecting'} />
                <span className="ws-mrow-val">{offline ? 'Offline' : CONN[conn]}</span>
              </span>
            </Row>
            <Row k="RTT" sub={net ? `usually ${fmtMs(net.sample.usualMs)}` : undefined}>
              <span className="ws-mrow-val" data-testid="rtt">
                {ms(connected ? rtt : null)}
              </span>
            </Row>
            <Row k="Echo" sub={stats.echo.p95 !== null ? `p50 · ${ms(stats.echo.p95)} p95` : undefined}>
              <span className="ws-mrow-val" data-testid="echo">
                {ms(stats.echo.p50)}
              </span>
            </Row>
            <Row k="Net" sub={!net ? 'measuring…' : net.state === 'good' ? 'both legs normal' : cmxSide(net.sample) ? `slow between cmx and ${machine.id}` : 'slow on this network'}>
              <span className="ws-mrow-val" data-testid="net-chip" data-state={net?.state ?? 'unknown'}>
                {!net ? '–' : net.state === 'good' ? 'Good' : net.state === 'busy' ? 'Busy' : 'Poor'}
              </span>
            </Row>
          </div>
          <Button
            size="sm"
            className="ws-mpop-more"
            onClick={() => {
              hide();
              a.openMachine();
            }}
          >
            See all details
            <Ic icon={IconArrowUpRight} size={12} />
          </Button>
        </div>
      )}
    </div>
  );
}

/** Inside the hull of point `a` and rectangle `r` (4px larger): the union of the triangles from `a` to each of its edges. */
function inCone(a: { x: number; y: number }, r: DOMRect, x: number, y: number) {
  const l = r.left - 4, t = r.top - 4, rr = r.right + 4, b = r.bottom + 4;
  if (x >= l && x <= rr && y >= t && y <= b) return true;
  const c = [[l, t], [rr, t], [rr, b], [l, b]];
  const side = (px: number, py: number, qx: number, qy: number) => (qx - px) * (y - py) - (qy - py) * (x - px);
  for (let k = 0; k < 4; k++) {
    const [bx, by] = c[k], [dx, dy] = c[(k + 1) % 4];
    const s1 = side(a.x, a.y, bx, by), s2 = side(bx, by, dx, dy), s3 = side(dx, dy, a.x, a.y);
    if ((s1 >= 0 && s2 >= 0 && s3 >= 0) || (s1 <= 0 && s2 <= 0 && s3 <= 0)) return true;
  }
  return false;
}
